import { FC, useState } from 'react';
import { Control, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { getS3FileOriginalName } from '../../../utils/get-s3file-original-name.util';
import { getTranslation } from '../../../utils/get-translation.util';
import { AddDividendFormData } from './add-dividend.validation';
import { DividendFileUpload, UploadedFile } from './dividend-file-upload';

type Props = {
  control: Control<AddDividendFormData>;
  getValues: UseFormGetValues<AddDividendFormData>;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
  setValue: UseFormSetValue<AddDividendFormData>;
};

const toUploadedFiles = (links: string[]): UploadedFile[] =>
  links.map((link) => ({
    abort: () => {},
    docLink: link,
    id: link,
    loadProgress: 100,
    name: getS3FileOriginalName(link) || 'document',
  }));

export const AddDividendStepFour: FC<Props> = ({
  control,
  getValues,
  onBack,
  onCancel,
  onNext,
  setValue,
}) => {
  const additionalNotes = useWatch({ control, name: 'additionalNotes' }) ?? '';

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(() =>
    toUploadedFiles(getValues('filesLinks') ?? []),
  );

  const handleFilesChange = (files: UploadedFile[]) => {
    setUploadedFiles(files);
    setValue('filesLinks', files.map((f) => f.docLink).filter(Boolean));
  };

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 pb-6 pt-4">
        <div className="flex flex-col gap-4">
          <span className="text-xs font-[450] text-gray-500">
            {getTranslation('GENERAL_DOCUMENTS_UPLOAD')}
          </span>
          <DividendFileUpload
            counter={
              uploadedFiles.length > 0 ? (
                <AppFormattedMessage
                  id="COUNT_FILES_UPLOADED"
                  values={{ count: uploadedFiles.filter(({ docLink }) => docLink).length }}
                />
              ) : undefined
            }
            files={uploadedFiles}
            onChange={handleFilesChange}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div
            className={twMerge(
              'relative rounded-t border-b bg-gray-50 px-2.5 py-2 focus-within:border-b-2 focus-within:border-brand-700',
              additionalNotes ? 'border-gray-700' : 'border-gray-600',
            )}
          >
            <span className="text-xs font-[450] text-gray-500">
              {getTranslation('ADDITIONAL_NOTES_OPTIONAL')}
            </span>
            <textarea
              className="min-h-[64px] w-full resize-none bg-transparent pt-1 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none"
              maxLength={3000}
              onChange={(e) => setValue('additionalNotes', e.target.value)}
              placeholder={getTranslation('DIVIDEND_NOTES_PLACEHOLDER')}
              value={additionalNotes}
            />
          </div>
          <span className="self-end text-[10px] font-medium text-gray-400">
            {additionalNotes.length}/3000
          </span>
        </div>
      </div>

      <div className="flex w-full shrink-0 items-center justify-between px-6">
        <Button
          className="h-9 w-fit bg-transparent px-3 py-1.5 text-sm font-[450] text-gray-700"
          onClick={onCancel}
          styleType="NONE"
          type="button"
        >
          {getTranslation('CANCEL')}
        </Button>
        <div className="flex items-center gap-3">
          <Button
            className="h-9 w-fit rounded border border-gray-300 bg-white px-4 py-2.5 text-sm font-[450] text-gray-700"
            onClick={onBack}
            styleType="NONE"
            type="button"
          >
            {getTranslation('BACK')}
          </Button>
          <Button
            className="h-9 w-fit px-4 py-2.5 text-sm font-[550] text-white"
            onClick={onNext}
            type="button"
          >
            {getTranslation('NEXT')}
          </Button>
        </div>
      </div>
    </>
  );
};
